import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { FiShoppingCart, FiUser, FiLogOut, FiPackage, FiSettings, FiX, FiHome, FiGrid } from 'react-icons/fi';
import OfferBanner from './OfferBanner';
import './Navbar.css';
import logo from "../../assets/images/LOGO-VED.png";

const Navbar = () => {
  const { user, logout } = useAuth();
  const { itemCount, setCartOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const updateOffset = () => {
      const banner = document.querySelector('.offer-banner');
      const nav = document.querySelector('.navbar');
      const bannerH = banner ? banner.offsetHeight : 0;
      const navH = nav ? nav.offsetHeight : 0;
      const total = bannerH + navH - 18;
      document.documentElement.style.setProperty('--drawer-top', `${total}px`);
      document.documentElement.style.setProperty('--drawer-height', `calc(100dvh - ${total}px)`);
    };
    updateOffset();
    window.addEventListener('resize', updateOffset);
    const observer = new ResizeObserver(updateOffset);
    const banner = document.querySelector('.offer-banner');
    if (banner) observer.observe(banner);
    return () => { window.removeEventListener('resize', updateOffset); observer.disconnect(); };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <>
      <OfferBanner paymentMethod="prepaid" />
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
        <div className="navbar__inner container">

          <button
            className="navbar__hamburger"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <span className="navbar__burger-bar" />
            <span className="navbar__burger-bar" />
            <span className="navbar__burger-bar" />
          </button>

          <Link to="/" className="navbar__logo">
            <img src={logo} alt="Vedonica International Logo" className="navbar__logo-img" />
          </Link>
          
          <div className="navbar__links">
            <Link to="/" className="navbar__link">Explore Products</Link>
          </div>

          <div className="navbar__actions">
            <div className="navbar__desktop-user">
              {user ? (
                <div className="navbar__user" ref={dropdownRef}>
                  <button className="navbar__user-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
                    <div className="navbar__avatar">{(user.first_name?.[0] || user.phone?.slice(-2, -1) || 'U').toUpperCase()}</div>
                    <span className="navbar__user-name">{user.first_name || user.phone?.slice(-4) || 'User'}</span>
                  </button>
                  {dropdownOpen && (
                    <div className="navbar__dropdown">
                      <div className="navbar__dropdown-header">
                        <p className="navbar__dropdown-name">{user.full_name || user.first_name || user.phone || 'User'}</p>
                        <p className="navbar__dropdown-email">{user.email}</p>
                      </div>
                      <div className="navbar__dropdown-divider" />
                      <Link to="/dashboard" className="navbar__dropdown-item"><FiUser size={16} /> My Account</Link>
                      <Link to="/dashboard?tab=orders" className="navbar__dropdown-item"><FiPackage size={16} /> My Orders</Link>
                      {user.is_admin && (
                        <Link to="/admin" className="navbar__dropdown-item navbar__dropdown-item--admin"><FiSettings size={16} /> Admin Panel</Link>
                      )}
                      <div className="navbar__dropdown-divider" />
                      <button onClick={handleLogout} className="navbar__dropdown-item navbar__dropdown-item--logout"><FiLogOut size={16} /> Sign Out</button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="navbar__auth">
                  <Link to="/login" className="navbar__link navbar__link--auth">Login</Link>
                  <Link to="/register" className="btn btn-primary navbar__cta">Get Started</Link>
                </div>
              )}
            </div>

            <div className="navbar__mobile-right">
              {user ? (
                <Link to="/dashboard" className="navbar__icon-btn" aria-label="Account">
                  <div className="navbar__avatar navbar__avatar--sm">{(user.first_name?.[0] || user.phone?.slice(-2, -1) || 'U').toUpperCase()}</div>
                </Link>
              ) : (
                <Link to="/login" className="navbar__mobile-signin" aria-label="Sign in">
                  <FiUser size={18} />
                  <span>Sign in</span>
                </Link>
              )}
            </div>

            <button className="navbar__icon-btn navbar__cart-btn" onClick={() => setCartOpen(true)} aria-label="Cart">
              <FiShoppingCart size={20} />
              {itemCount > 0 && <span className="navbar__cart-badge">{itemCount > 99 ? '99+' : itemCount}</span>}
            </button>
          </div>
        </div>
      </nav>

      {menuOpen && <div className="navbar__overlay" onClick={() => setMenuOpen(false)} />}

      <div className={`navbar__drawer ${menuOpen ? 'navbar__drawer--open' : ''}`}>
        <div className="navbar__drawer-header">
          {user ? (
            <div className="navbar__drawer-user">
              <div className="navbar__drawer-avatar">{(user.first_name?.[0] || user.phone?.slice(-2, -1) || 'U').toUpperCase()}</div>
              <div className="navbar__drawer-user-info">
                <p className="navbar__drawer-greeting">Hello, {user.first_name || user.phone?.slice(-4) || 'User'}</p>
                <p className="navbar__drawer-email">{user.email}</p>
              </div>
            </div>
          ) : (
            <div className="navbar__drawer-user">
              <div className="navbar__drawer-avatar navbar__drawer-avatar--guest"><FiUser size={20} /></div>
              <div className="navbar__drawer-user-info">
                <p className="navbar__drawer-greeting">Hello, Guest</p>
                <p className="navbar__drawer-email">Sign in to your account</p>
              </div>
            </div>
          )}
          <button className="navbar__drawer-close" onClick={() => setMenuOpen(false)} aria-label="Close">
            <FiX size={20} />
          </button>
        </div>

        <div className="navbar__drawer-body">
          <p className="navbar__drawer-section-label">Browse</p>
          <Link to="/" className="navbar__drawer-item">
            <FiHome size={17} /><span>Home</span>
          </Link>
          <Link to="/" className="navbar__drawer-item">
            <FiGrid size={17} /><span>All Products</span>
          </Link>

          <div className="navbar__drawer-divider" />
          <p className="navbar__drawer-section-label">Account</p>

          {user ? (
            <>
              <Link to="/dashboard" className="navbar__drawer-item"><FiUser size={17} /><span>My Account</span></Link>
              <Link to="/dashboard?tab=orders" className="navbar__drawer-item"><FiPackage size={17} /><span>My Orders</span></Link>
              {user.is_admin && (
                <Link to="/admin" className="navbar__drawer-item navbar__drawer-item--admin"><FiSettings size={17} /><span>Admin Panel</span></Link>
              )}
              <div className="navbar__drawer-divider" />
              <button onClick={handleLogout} className="navbar__drawer-item navbar__drawer-item--logout">
                <FiLogOut size={17} /><span>Sign Out</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="navbar__drawer-item"><FiUser size={17} /><span>Login</span></Link>
              <Link to="/register" className="navbar__drawer-cta">Get Started</Link>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;